import _toConsumableArray from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _objectWithoutProperties from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _defineProperty from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _classCallCheck from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/createClass.js";
var _excluded = ["willChange"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import sync, { cancelSync } from 'framesync';
import { invariant } from 'hey-listen';
import { createElement } from 'react';
import { featureDefinitions } from '../motion/features/definitions.mjs';
import { createBox } from '../projection/geometry/models.mjs';
import { isRefObject } from '../utils/is-ref-object.mjs';
import { env } from '../utils/process.mjs';
import { initPrefersReducedMotion } from '../utils/reduced-motion/index.mjs';
import { hasReducedMotionListener, prefersReducedMotion } from '../utils/reduced-motion/state.mjs';
import { SubscriptionManager } from '../utils/subscription-manager.mjs';
import { motionValue } from '../value/index.mjs';
import { isWillChangeMotionValue } from '../value/use-will-change/is.mjs';
import { isMotionValue } from '../value/utils/is-motion-value.mjs';
import { transformProps } from './html/utils/transform.mjs';
import { variantPriorityOrder } from './utils/animation-state.mjs';
import { isControllingVariants, isVariantNode } from './utils/is-controlling-variants.mjs';
import { isVariantLabel } from './utils/is-variant-label.mjs';
import { updateMotionValuesFromProps } from './utils/motion-values.mjs';
import { resolveVariantFromProps } from './utils/resolve-variants.mjs';
var featureNames = Object.keys(featureDefinitions);
var numFeatures = featureNames.length;
var propEventHandlers = ["AnimationStart", "AnimationComplete", "Update", "Unmount", "BeforeLayoutMeasure", "LayoutMeasure", "LayoutAnimationStart", "LayoutAnimationComplete"];
/**
 * A VisualElement is an imperative abstraction around UI elements such as
 * HTMLElement, SVGElement, Three.Object3D etc.
 */
var VisualElement = /*#__PURE__*/function () {
  function VisualElement(_ref) {
    var _this = this;
    var parent = _ref.parent,
      props = _ref.props,
      reducedMotionConfig = _ref.reducedMotionConfig,
      visualState = _ref.visualState;
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    _classCallCheck(this, VisualElement);
    /**
     * A reference to the current underlying Instance, e.g. a HTMLElement
     * or Three.Mesh etc.
     */
    this.current = null;
    /**
     * A set containing references to this VisualElement's children.
     */
    this.children = new Set();
    /**
     * Determine what role this visual element should take in the variant tree.
     */
    this.isVariantNode = false;
    this.isControllingVariants = false;
    /**
     * Decides whether this VisualElement should animate in reduced motion
     * mode.
     *
     * TODO: This is currently set on every individual VisualElement but feels
     * like it could be set globally.
     */
    this.shouldReduceMotion = null;
    /**
     * A map of all motion values attached to this visual element. Motion
     * values are source of truth for any given animated value. A motion
     * value might be provided externally by the component via props.
     */
    this.values = new Map();
    /**
     * Tracks whether this VisualElement's React component is currently present
     * within the defined React tree.
     */
    this.isPresent = true;
    /**
     * A map of every subscription that binds the provided or generated
     * motion values onChange listeners to this visual element.
     */
    this.valueSubscriptions = new Map();
    /**
     * A reference to the previously-provided motion values as returned
     * from scrapeMotionValuesFromProps. We use the keys in here to determine
     * if any motion values need to be removed after props are updated.
     */
    this.prevMotionValues = {};
    /**
     * An object containing a SubscriptionManager for each active event.
     */
    this.events = {};
    /**
     * An object containing an unsubscribe function for each prop event subscription.
     * For example, every "Update" event can have multiple subscribers via
     * VisualElement.on(), but only one of those can be defined via the onUpdate prop.
     */
    this.propEventSubscriptions = {};
    this.notifyUpdate = function () {
      return _this.notify("Update", _this.latestValues);
    };
    this.render = function () {
      if (!_this.current) return;
      _this.triggerBuild();
      _this.renderInstance(_this.current, _this.renderState, _this.props.style, _this.projection);
    };
    this.scheduleRender = function () {
      return sync.render(_this.render, false, true);
    };
    var latestValues = visualState.latestValues,
      renderState = visualState.renderState;
    this.latestValues = latestValues;
    this.baseTarget = _objectSpread({}, latestValues);
    this.initialValues = props.initial ? _objectSpread({}, latestValues) : {};
    this.renderState = renderState;
    this.parent = parent;
    this.props = props;
    this.depth = parent ? parent.depth + 1 : 0;
    this.reducedMotionConfig = reducedMotionConfig;
    this.options = options;
    this.isControllingVariants = isControllingVariants(props);
    this.isVariantNode = isVariantNode(props);
    if (this.isVariantNode) {
      this.variantChildren = new Set();
    }
    this.manuallyAnimateOnMount = Boolean(parent && parent.current);
    /**
     * Any motion values that are provided to the element when created
     * aren't yet bound to the element, as this would technically be impure.
     * However, we iterate through the motion values and set them to the
     * initial values for this component.
     *
     * TODO: This is impure and we should look at changing this to run on mount.
     * Doing so will break some tests but this isn't neccessarily a breaking change,
     * more a reflection of the test.
     */
    var _this$scrapeMotionVal = this.scrapeMotionValuesFromProps(props),
      willChange = _this$scrapeMotionVal.willChange,
      initialMotionValues = _objectWithoutProperties(_this$scrapeMotionVal, _excluded);
    for (var key in initialMotionValues) {
      var value = initialMotionValues[key];
      if (latestValues[key] !== undefined && isMotionValue(value)) {
        value.set(latestValues[key], false);
        if (isWillChangeMotionValue(willChange)) {
          willChange.add(key);
        }
      }
    }
  }
  /**
   * This method takes React props and returns found MotionValues. For example, HTML
   * MotionValues will be found within the style prop, whereas for Three.js within attribute arrays.
   *
   * This isn't an abstract method as it needs calling in the constructor, but it is
   * intended to be one.
   */
  _createClass(VisualElement, [{
    key: "scrapeMotionValuesFromProps",
    value: function scrapeMotionValuesFromProps(_props) {
      return {};
    }
  }, {
    key: "mount",
    value: function mount(instance) {
      var _this2 = this;
      var _a;
      this.current = instance;
      if (this.projection) {
        this.projection.mount(instance);
      }
      if (this.parent && this.isVariantNode && !this.isControllingVariants) {
        this.removeFromVariantTree = (_a = this.parent) === null || _a === void 0 ? void 0 : _a.addVariantChild(this);
      }
      this.values.forEach(function (value, key) {
        return _this2.bindToMotionValue(key, value);
      });
      if (!hasReducedMotionListener.current) {
        initPrefersReducedMotion();
      }
      this.shouldReduceMotion = this.reducedMotionConfig === "never" ? false : this.reducedMotionConfig === "always" ? true : prefersReducedMotion.current;
      if (this.parent) this.parent.children.add(this);
      this.setProps(this.props);
    }
  }, {
    key: "unmount",
    value: function unmount() {
      var _a, _b, _c;
      (_a = this.projection) === null || _a === void 0 ? void 0 : _a.unmount();
      cancelSync.update(this.notifyUpdate);
      cancelSync.render(this.render);
      this.valueSubscriptions.forEach(function (remove) {
        return remove();
      });
      (_b = this.removeFromVariantTree) === null || _b === void 0 ? void 0 : _b.call(this);
      (_c = this.parent) === null || _c === void 0 ? void 0 : _c.children.delete(this);
      for (var key in this.events) {
        this.events[key].clear();
      }
      this.current = null;
    }
  }, {
    key: "bindToMotionValue",
    value: function bindToMotionValue(key, value) {
      var _this3 = this;
      var valueIsTransform = transformProps.has(key);
      var removeOnChange = value.onChange(function (latestValue) {
        _this3.latestValues[key] = latestValue;
        _this3.props.onUpdate && sync.update(_this3.notifyUpdate, false, true);
        if (valueIsTransform && _this3.projection) {
          _this3.projection.isTransformDirty = true;
        }
      });
      var removeOnRenderRequest = value.onRenderRequest(this.scheduleRender);
      this.valueSubscriptions.set(key, function () {
        removeOnChange();
        removeOnRenderRequest();
      });
    }
  }, {
    key: "sortNodePosition",
    value: function sortNodePosition(other) {
      /**
       * If these nodes aren't even of the same type we can't compare their depth.
       */
      if (!this.current || !this.sortInstanceNodePosition || this.type !== other.type) return 0;
      return this.sortInstanceNodePosition(this.current, other.current);
    }
  }, {
    key: "loadFeatures",
    value: function loadFeatures(renderedProps, isStrict, preloadedFeatures, projectionId, ProjectionNodeConstructor, initialLayoutGroupConfig) {
      var _this4 = this;
      var features = [];
      /**
       * If we're in development mode, check to make sure we're not rendering a motion component
       * as a child of LazyMotion, as this will break the file-size benefits of using it.
       */
      if (env !== "production" && preloadedFeatures && isStrict) {
        invariant(false, "You have rendered a `motion` component within a `LazyMotion` component. This will break tree shaking. Import and render a `m` component instead.");
      }
      for (var i = 0; i < numFeatures; i++) {
        var name = featureNames[i];
        var _featureDefinitions$n = featureDefinitions[name],
          isEnabled = _featureDefinitions$n.isEnabled,
          Component = _featureDefinitions$n.Component;
        /**
         * It might be possible in the future to use this moment to
         * dynamically request functionality. In initial tests this
         * was producing a lot of duplication amongst bundles.
         */
        if (isEnabled(renderedProps) && Component) {
          features.push(createElement(Component, _objectSpread(_objectSpread({
            key: name
          }, renderedProps), {}, {
            visualElement: this
          })));
        }
      }
      if (!this.projection && ProjectionNodeConstructor) {
        this.projection = new ProjectionNodeConstructor(projectionId, this.latestValues, this.parent && this.parent.projection);
        var layoutId = renderedProps.layoutId,
          layout = renderedProps.layout,
          drag = renderedProps.drag,
          dragConstraints = renderedProps.dragConstraints,
          layoutScroll = renderedProps.layoutScroll;
        this.projection.setOptions({
          layoutId: layoutId,
          layout: layout,
          alwaysMeasureLayout: Boolean(drag) || dragConstraints && isRefObject(dragConstraints),
          visualElement: this,
          scheduleRender: function scheduleRender() {
            return _this4.scheduleRender();
          },
          /**
           * TODO: Update options in an effect. This could be tricky as it'll be too late
           * to update by the time layout animations run.
           * We also need to fix this safeToRemove by linking it up to the one returned by usePresence,
           * ensuring it gets called if there's no potential layout animations.
           *
           */
          animationType: typeof layout === "string" ? layout : "both",
          initialPromotionConfig: initialLayoutGroupConfig,
          layoutScroll: layoutScroll
        });
      }
      return features;
    }
  }, {
    key: "triggerBuild",
    value: function triggerBuild() {
      this.build(this.renderState, this.latestValues, this.options, this.props);
    }
    /**
     * Measure the current viewport box with or without transforms.
     * Only measures axis-aligned boxes, rotate and skew must be manually
     * removed with a re-render to work.
     */
  }, {
    key: "measureViewportBox",
    value: function measureViewportBox() {
      return this.current ? this.measureInstanceViewportBox(this.current, this.props) : createBox();
    }
  }, {
    key: "getStaticValue",
    value: function getStaticValue(key) {
      return this.latestValues[key];
    }
  }, {
    key: "setStaticValue",
    value: function setStaticValue(key, value) {
      this.latestValues[key] = value;
    }
    /**
     * Make a target animatable by Popmotion. For instance, if we're
     * trying to animate width from 100px to 100vw we need to measure 100vw
     * in pixels to determine what we really need to animate to. This is also
     * pluggable to support Framer's custom value types like Color,
     * and CSS variables.
     */
  }, {
    key: "makeTargetAnimatable",
    value: function makeTargetAnimatable(target) {
      var canMutate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      return this.makeTargetAnimatableFromInstance(target, this.props, canMutate);
    }
    /**
     * Update the provided props. Ensure any newly-added motion values are
     * added to our map, old ones removed, and listeners updated.
     */
  }, {
    key: "setProps",
    value: function setProps(props) {
      if (props.transformTemplate || this.props.transformTemplate) {
        this.scheduleRender();
      }
      this.props = props;
      /**
       * Update prop event handlers ie onAnimationStart, onAnimationComplete
       */
      for (var i = 0; i < propEventHandlers.length; i++) {
        var key = propEventHandlers[i];
        if (this.propEventSubscriptions[key]) {
          this.propEventSubscriptions[key]();
          delete this.propEventSubscriptions[key];
        }
        var listener = props["on" + key];
        if (listener) {
          this.propEventSubscriptions[key] = this.on(key, listener);
        }
      }
      this.prevMotionValues = updateMotionValuesFromProps(this, this.scrapeMotionValuesFromProps(props), this.prevMotionValues);
    }
  }, {
    key: "getProps",
    value: function getProps() {
      return this.props;
    }
    /**
     * Returns the variant definition with a given name.
     */
  }, {
    key: "getVariant",
    value: function getVariant(name) {
      var _a;
      return (_a = this.props.variants) === null || _a === void 0 ? void 0 : _a[name];
    }
    /**
     * Returns the defined default transition on this component.
     */
  }, {
    key: "getDefaultTransition",
    value: function getDefaultTransition() {
      return this.props.transition;
    }
  }, {
    key: "getTransformPagePoint",
    value: function getTransformPagePoint() {
      return this.props.transformPagePoint;
    }
  }, {
    key: "getClosestVariantNode",
    value: function getClosestVariantNode() {
      var _a;
      return this.isVariantNode ? this : (_a = this.parent) === null || _a === void 0 ? void 0 : _a.getClosestVariantNode();
    }
  }, {
    key: "getVariantContext",
    value: function getVariantContext() {
      var startAtParent = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      var _a, _b;
      if (startAtParent) return (_a = this.parent) === null || _a === void 0 ? void 0 : _a.getVariantContext();
      if (!this.isControllingVariants) {
        var _context = ((_b = this.parent) === null || _b === void 0 ? void 0 : _b.getVariantContext()) || {};
        if (this.props.initial !== undefined) {
          _context.initial = this.props.initial;
        }
        return _context;
      }
      var context = {};
      for (var i = 0; i < numVariantProps; i++) {
        var name = variantProps[i];
        var prop = this.props[name];
        if (isVariantLabel(prop) || prop === false) {
          context[name] = prop;
        }
      }
      return context;
    }
    /**
     * Add a child visual element to our set of children.
     */
  }, {
    key: "addVariantChild",
    value: function addVariantChild(child) {
      var _a;
      var closestVariantNode = this.getClosestVariantNode();
      if (closestVariantNode) {
        (_a = closestVariantNode.variantChildren) === null || _a === void 0 ? void 0 : _a.add(child);
        return function () {
          return closestVariantNode.variantChildren.delete(child);
        };
      }
    }
    /**
     * Add a motion value and bind it to this visual element.
     */
  }, {
    key: "addValue",
    value: function addValue(key, value) {
      // Remove existing value if it exists
      if (this.hasValue(key)) this.removeValue(key);
      this.values.set(key, value);
      this.latestValues[key] = value.get();
      this.bindToMotionValue(key, value);
    }
    /**
     * Remove a motion value and unbind any active subscriptions.
     */
  }, {
    key: "removeValue",
    value: function removeValue(key) {
      var _a;
      this.values.delete(key);
      (_a = this.valueSubscriptions.get(key)) === null || _a === void 0 ? void 0 : _a();
      this.valueSubscriptions.delete(key);
      delete this.latestValues[key];
      this.removeValueFromRenderState(key, this.renderState);
    }
    /**
     * Check whether we have a motion value for this key
     */
  }, {
    key: "hasValue",
    value: function hasValue(key) {
      return this.values.has(key);
    }
    /**
     * Get a motion value for this key. If called with a default
     * value, we'll create one if none exists.
     */
  }, {
    key: "getValue",
    value: function getValue(key, defaultValue) {
      if (this.props.values && this.props.values[key]) {
        return this.props.values[key];
      }
      var value = this.values.get(key);
      if (value === undefined && defaultValue !== undefined) {
        value = motionValue(defaultValue);
        this.addValue(key, value);
      }
      return value;
    }
    /**
     * If we're trying to animate to a previously unencountered value,
     * we need to check for it in our state and as a last resort read it
     * directly from the instance (which might have performance implications).
     */
  }, {
    key: "readValue",
    value: function readValue(key) {
      return this.latestValues[key] !== undefined || !this.current ? this.latestValues[key] : this.readValueFromInstance(this.current, key, this.options);
    }
    /**
     * Set the base target to later animate back to. This is currently
     * only hydrated on creation and when we first read a value.
     */
  }, {
    key: "setBaseTarget",
    value: function setBaseTarget(key, value) {
      this.baseTarget[key] = value;
    }
    /**
     * Find the base target for a value thats been removed from all animation
     * props.
     */
  }, {
    key: "getBaseTarget",
    value: function getBaseTarget(key) {
      var _a;
      var initial = this.props.initial;
      var valueFromInitial = typeof initial === "string" || typeof initial === "object" ? (_a = resolveVariantFromProps(this.props, initial)) === null || _a === void 0 ? void 0 : _a[key] : undefined;
      /**
       * If this value still exists in the current initial variant, read that.
       */
      if (initial && valueFromInitial !== undefined) {
        return valueFromInitial;
      }
      /**
       * Alternatively, if this VisualElement config has defined a getBaseTarget
       * so we can read the value from an alternative source, try that.
       */
      var target = this.getBaseTargetFromProps(this.props, key);
      if (target !== undefined && !isMotionValue(target)) return target;
      /**
       * If the value was initially defined on initial, but it doesn't any more,
       * return undefined. Otherwise return the value as initially read from the DOM.
       */
      return this.initialValues[key] !== undefined && valueFromInitial === undefined ? undefined : this.baseTarget[key];
    }
  }, {
    key: "on",
    value: function on(eventName, callback) {
      if (!this.events[eventName]) {
        this.events[eventName] = new SubscriptionManager();
      }
      return this.events[eventName].add(callback);
    }
  }, {
    key: "notify",
    value: function notify(eventName) {
      var _a2;
      var _a;
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }
      (_a = this.events[eventName]) === null || _a === void 0 ? void 0 : (_a2 = _a).notify.apply(_a2, args);
    }
  }]);
  return VisualElement;
}();
var variantProps = ["initial"].concat(_toConsumableArray(variantPriorityOrder));
var numVariantProps = variantProps.length;
export { VisualElement };